/**
 * Submit API service file
 *
 * @package    src/services
 * @author     WangTuring <wangwang@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    2.0
 * @link       https://turing.ly/api/submit/
 */
import axios from 'axios'
import { ApiConstant } from '../constants'

/**
 * Add agent information that includes browser, ip location, ads, etc
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   agentInfo   agent information
 * @return  ret
 */
export function addAgentInfo(agentInfo) {
  let token = localStorage.getItem('token')

  if (token) {
    let header = {
      'Content-Type': 'application/json',
      'Authorization': token
    }

    return axios.post(ApiConstant.BASE_URL + ApiConstant.ADD_AGENT_INFO, agentInfo, { headers: header })
  } else {
    return axios.post(ApiConstant.BASE_URL + ApiConstant.ADD_AGENT_INFO, agentInfo)
  }
}

/**
 * Add agent data that includes browser, ip location, ads, etc
 *
 * @author  DongTuring <dong@turing.com>
 * @param   string   agentInfo   agent information
 * @return  ret
 */
export function addAgentData(agentData) {
  let token = localStorage.getItem('token')

  if (token) {
    let header = {
      'Content-Type': 'application/json',
      'Authorization': token
    }

    return axios.post(ApiConstant.BASE_URL + ApiConstant.ADD_AGENT_DATA, agentData, { headers: header })
  } else {
    return axios.post(ApiConstant.BASE_URL + ApiConstant.ADD_AGENT_DATA, agentData)
  }
}

/**
 * Save remote experience info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   object   data    remote experience data
 * @return                
 */
export function saveRemoteExpInfo(data) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_REMOTE_EXP_INFO, data, { headers: header })
}

/**
 * Fetch remote experience info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return                
 */
export function getRemoteExpInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_REMOTE_EXP_INFO, { headers: header })
}

/**
 * Save English experience info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   object   data    English experience data
 * @return                
 */
export function saveEnglishExpInfo(data) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_ENGLISH_EXP_INFO, data, { headers: header })
}


/**
 * Save JavaScript test type
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   type   Javascript test type
 * @return  
 */
export function saveJavaScriptTestType(type) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    type: type
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_JAVASCRIPT_TEST_TYPE, data, { headers: header })
}

/**
 * Save selected language
 *
 * @author  YingTuring <ying@turing.com>
 * @param   string   languageName   selected lanuage name
 * @return  ret
 */
export function saveSelectedLanguage(languageName) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    from: 'flow',
    testName: languageName
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_SELECTED_LANGUAGE, data, { headers: header })
}

/**
 * Save test result
 *
 * @author  YingTuring <ying@turing.com>
 * @param   object   resultData   test result data
 * @param   string   fromWhere    where are you from
 * @return  ret
 */
export function saveTestResult(resultData, fromWhere) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    from: fromWhere,
    testResult: resultData
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_TEST_RESULT, data, { headers: header })
}

/**
 * Save test result
 *
 * @author  YingTuring <ying@turing.com>
 * @param   object   resultData   test result data
 * @param   string   fromWhere    where are you from
 * @return  ret
 */
export function saveTestResponseTime(data) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_TEST_RESPONSE_TIME, data, { headers: header })
}

/**
 * Save profile info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   object  profile data
 * @return  object              
 */
export function saveProfileInfo(formData) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_PROFILE_INFO, formData, { headers: header })
}

/**
 * Fetch profile info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return                
 */
export function getProfileInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_PROFILE_INFO, { headers: header })
}

/**
 * Save not developer profile info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   object  not developer profile data
 * @return  object              
 */
export function saveNotdevProfileInfo(data) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_NOTDEV_PROFILE_INFO, data, { headers: header })
}


/**
 * Fetch not developer profile info
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return                
 */
export function getNotdevProfileInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_NOTDEV_PROFILE_INFO, { headers: header })
}

/**
 * Get initialize info on resume page
 *
 * @author  YingTuring <ying@turing.com>
 * @return  ret
 */
export function getResumeInfo(resumeData) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_RESUME_INFO, { headers: header })
}

/**
 * Save resume file
 *
 * @author  YingTuring <ying@turing.com>
 * @param   file   file data
 * @return  ret
 */
export function saveResumeFile(fileData) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  const formData = new FormData()
  formData.append('file', fileData)

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_RESUME_FILE, formData, { headers: header })
}

/**
 * Save other info on resume page
 *
 * @author  YingTuring <ying@turing.com>
 * @param   object   resumeData   
 * @return  ret
 */
export function saveResumeInfo(resumeData) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_RESUME_INFO, resumeData, { headers: header })
}

/**
 * Save jobs and levels
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   jobList
 * @param   string   levelList
 * @return  ret
 */
export function saveJobsInfo(jobList, levelList) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    jobList: jobList,
    levelList: levelList
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_JOBS_INFO, data, { headers: header })
}

/**
 * Get jobs and skills info for user
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return  object              
 */
export function getJobsSkillsInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_JOBS_SKILLS_INFO, { headers: header })
}

/**
 * Save skills and levels
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   string   skillList
 * @param   string   levelList
 * @return  ret
 */
export function saveSkillsInfo(skillList, levelList) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    skillList: skillList,
    levelList: levelList
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_SKILLS_INFO, data, { headers: header })
}

/**
 * Submit with linkedin data
 *
 * @author  DongTuring <dong@turing.com>
 * @return  object              
 */
export function submitLinkedinData(data) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || '' 
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_LINKEDIN_DATA, data, { headers: header })
}

/**
 * Submit with linkedin data
 *
 * @author  DongTuring <dong@turing.com>
 * @return  object              
 */
export function submitLinkedinStatus(status) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }
  
  let data = {
    status: status
  }
  
  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_LINKEDIN_STATUS, data, { headers: header })
}

/**
 * Submit Github data
 *
 * @author  YingTuring <dong@turing.com>
 * @return  object              
 */
export function submitGitHubData(loginData) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''  
  }

  let data = {
    githubCode : loginData.code,
    githubId: loginData.githubId,
    githubSecret: loginData.githubSecret
  }
  
  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_GITHUB_DATA, data, { headers: header })
}

/**
 * Get base jobs and levels
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return  object              
 */
export function getBaseJobsInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_BASE_JOBS_INFO, { headers: header })
}

/**
 * Get base skills and levels
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return  object              
 */
export function getBaseSkillsInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || '' 
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_BASE_SKILLS_INFO, { headers: header })
}

/**
 * Get base all skills
 *
 * @author  WangTuring <wangwang@turing.com>
 * @param   
 * @return  object              
 */
export function getBaseAllSkillsInfo() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || '' 
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_BASE_ALL_SKILLS_INFO, { headers: header })
}

/**
 * Save taking home challenge option
 *
 * @author  YingTuring <ying@turing.com>
 * @param   int   optionNumber   selected option number
 * @return  ret
 */
export function saveHomeChallengeOption(optionNumber) {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  let data = {
    option: optionNumber
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.SAVE_HOME_CHALLENGE_OPTION, data, { headers: header })
}

/**
 * Get selected test language name 
 *
 * @author  DongTuring <dong@turing.com>
 * @param   
 * @return  object              
 */
export function getSelectedLanguage() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.get(ApiConstant.BASE_URL + ApiConstant.GET_SELECTED_LANGUAGE, { headers: header })
}

/**
 * Unlock challenges
 *
 * @author  YingTuring <ying@turing.com>
 * @param   object   resultData   test result data
 * @param   string   fromWhere    where are you from
 * @return  ret
 */
export function unlockChallenges() {
  let header = {
    'Content-Type': 'application/json',
    'Authorization': localStorage.getItem('token') || ''
  }

  return axios.post(ApiConstant.BASE_URL + ApiConstant.UNLOCK_CHALLENGE, null, { headers: header })
}